import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import {
  usePaymentVoucher,
  usePaymentVoucherStore,
  useCustomer,
} from "hooks";
import {
  PaymentVoucherItem,
  PaymentVoucherQuery,
  PaymentVoucherRow,
} from "types";
import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";
import { Plus } from "lucide-react";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch (error) {
      // ignore invalid JSON strings
    }
    return trimmed
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const parseNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeItems = (items?: PaymentVoucherItem[]) =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          amount: parseNumber(item.amount),
          partner_id:
            item.partner_id !== undefined &&
            item.partner_id !== null &&
            item.partner_id !== ""
              ? Number(item.partner_id)
              : undefined,
          reason: item.reason ? String(item.reason).trim() : undefined,
          description: item.description
            ? String(item.description).trim()
            : undefined,
        }))
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            Boolean(item.reason) ||
            Boolean(item.description),
        )
    : [];

const withItemFields = (voucher: PaymentVoucherRow): PaymentVoucherRow => {
  const normalizedItems = sanitizeItems(voucher.items);
  const firstItem = normalizedItems[0];
  return {
    ...voucher,
    attachments: normalizeAttachments(voucher.attachments).join(", "),
    items: normalizedItems,
    amount:
      voucher.amount ??
      (typeof firstItem?.amount === "number"
        ? firstItem.amount
        : firstItem?.amount
          ? Number(firstItem.amount)
          : undefined),
    partner_id: voucher.partner_id ?? firstItem?.partner_id,
    reason: voucher.reason ?? firstItem?.reason,
    item_description: voucher.item_description ?? firstItem?.description,
  };
};

const PaymentVouchers: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<PaymentVoucherQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<PaymentVoucherRow | null>(null);
  const [selectedItems, setSelectedItems] = useState<PaymentVoucherItem[]>([]);
  const [selectedVoucherLabel, setSelectedVoucherLabel] = useState<string>("");

  const {
    rows,
    setRows,
    removeRow,
    isLoading,
    setLoading,
  } = usePaymentVoucherStore();

  const { fetchList, deletePaymentVoucher } = usePaymentVoucher();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const {
    data: paymentVoucherData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  useEffect(() => {
    if (paymentVoucherData?.data) {
      const mapped =
        (paymentVoucherData.data as PaymentVoucherRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedVoucherLabel(
          mapped[0].original_voucher_no ??
            mapped[0].object_name ??
            `Phiếu ${mapped[0].id}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedVoucherLabel("");
      }
    }
  }, [paymentVoucherData, setRows]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deletePaymentVoucher.isPending));
  }, [deletePaymentVoucher.isPending, isFetching, setLoading]);

  const customerLabelById = useMemo(() => {
    const map = new Map<number | string, string>();
    (customersData?.data as any[])?.forEach((c) => {
      const label = `${c.code}${c.name ? ` - ${c.name}` : ""}`;
      map.set(c.id, label);
    });
    return map;
  }, [customersData]);

  const columns = useMemo<ColDef<PaymentVoucherRow>[]>(() => {
    return [
      {
        headerName: "Ngày chi",
        field: "payment_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Số phiếu",
        field: "original_voucher_no",
        colId: "code",
        minWidth: 140,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Đối tượng",
        field: "object_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Người nhận",
        field: "receiver_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Tệp đính kèm",
        field: "attachments",
        minWidth: 180,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Hóa đơn nhập",
        field: "import_invoice_id",
        minWidth: 150,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
      },
      // {
      //   headerName: "Mã đối tác",
      //   field: "partner_id",
      //   minWidth: 140,
      //   filter: "agNumberColumnFilter",
      //   valueFormatter: (params) => {
      //     if (params.value === undefined || params.value === null) return "";
      //     const label = customerLabelById.get(params.value);
      //     return label ? `${label} (${params.value})` : String(params.value);
      //   },
      // },
      // {
      //   headerName: "Lý do chi",
      //   field: "reason",
      //   minWidth: 180,
      //   flex: 1,
      //   filter: "agTextColumnFilter",
      // },
      // {
      //   headerName: "Mô tả chi tiết",
      //   field: "item_description",
      //   minWidth: 200,
      //   flex: 1,
      //   filter: "agTextColumnFilter",
      // },
    ];
  }, []);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const handleAdd = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.CASH_PAYMENT_CREATE);
  }, [navigate]);

  const handleEditRow = useCallback(
    (params: any) => {
      if (params?.data?.id) {
        navigate(
          `${ROUTES.ACCOUNTING.CASH_PAYMENT_DETAIL}/${params.data.id}`,
        );
      }
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<PaymentVoucherRow>) => {
      console.log(event,'event')
      const items = sanitizeItems(event.data?.items);
      setSelectedItems(items);
      setSelectedVoucherLabel(
        event.data?.original_voucher_no ??
          event.data?.object_name ??
          `Phiếu ${event.data?.id ?? ""}`,
      );
    },
    [],
  );

  const handleDeleteRow = useCallback((row: PaymentVoucherRow) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deletePaymentVoucher.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
        refetch();
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu chi không thành công");
      },
    });
  }, [confirmRow, deletePaymentVoucher, refetch, removeRow]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
      editable: false,
    }),
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Kế toán] Phiếu chi
        </h1>
        <p className="text-sm text-gray-600">
          Danh sách phiếu chi. Bấm Thêm hoặc nhấp vào từng dòng để chỉnh sửa trên trang chi tiết.
        </p>
      </div>

      <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-end">
          {/* <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu chi
            </h2>
            <p className="text-xs text-gray-500">
              Quản lý phiếu chi và chuyển tới trang chi tiết để thêm/cập nhật.
            </p>
          </div> */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 text-green-700" />
              Thêm phiếu chi
            </button>
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Chi tiết dòng chi{" "}
              {selectedVoucherLabel ? `- ${selectedVoucherLabel}` : ""}
            </h3>
            {/* <p className="text-xs text-gray-500">
              Chỉ xem, không chỉnh sửa. Chọn dòng phiếu chi để xem các khoản chi.
            </p> */}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Mã đối tác
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Lý do chi
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">Mô tả</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length ? (
                  selectedItems.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {Number(item.amount ?? 0).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.partner_id
                          ? customerLabelById.get(item.partner_id) ??
                            String(item.partner_id)
                          : ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.reason ?? ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.description ?? ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn một phiếu chi để xem chi tiết dòng chi.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={620}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={paymentVoucherData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
        />

        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu chi, vui lòng chờ giây lát."
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu chi?"
        description={`Bạn chắc chắn muốn xóa phiếu chi "${confirmRow?.original_voucher_no ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deletePaymentVoucher.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default PaymentVouchers;
